import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from '@/i18n'
import { ListFilter, Save, Search } from 'lucide-react'
import { toast } from './toast'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { notifyAnnotatorBlocked } from '@/service/annotatorAccess'
import AppSidebar from '@/components/AppSidebar'
import DataSidebar from '@/components/DataSidebar'
import { IntuiBreadcrumb } from '@/components/intueri/IntuiBreadcrumb'
import SourceFilePreviewOverlay from './SourceFilePreviewOverlay'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiTabs,
  IntuiTabsList,
  IntuiTabsTrigger,
} from '@/components/intueri/IntuiTabs'
import KnowledgeGraphCanvas, {
  type CanvasHandle,
} from '@/routes/KnowledgeGraph/KnowledgeGraphCanvas'
import KgZoomControls from '@/routes/KnowledgeGraph/ZoomControls'
import NodeDetailsPanel from '@/routes/KnowledgeGraph/NodeDetailsPanel'
import TypesPanel from '@/routes/KnowledgeGraph/TypesPanel'
import { NODE_TYPES } from '@/routes/KnowledgeGraph/nodeIcons'
import type { MockEdge, MockNode, MockNodeType } from '@/routes/KnowledgeGraph/types'
import {
  apiService,
  type MatchedTagGroup,
  type TagDocumentRow,
  type TagSheetRow,
  type UkgHierarchySite,
} from '@/service/api'
import SheetsCanvas from './SheetsCanvas'
import DocumentsPanel from './DocumentsPanel'
import { defaultLayout, type CardPosition } from './boardLayout'
import { connectedSources, type ConnectedSource } from './connectedSources'
import { PAGE_HEADER } from '@/components/layout/pageHeader'

type ViewMode = 'board' | 'knowledge_graph' | 'documents'

/** The tag itself, then one node per sheet it appears on. */
const TAG_NODE_ID = 'tag-root'

/**
 * The factor `CanvasHandle.fit()` zooms past a true fit by (see its own comment
 * in KnowledgeGraphCanvas). Kept as a named constant so the reason this page
 * divides it back out is legible, and so it is one edit if that calibration
 * ever changes.
 */
const FIT_OVERSHOOT = 1.73

/**
 * Tag mapping drill-down (FS-655) — where one tag actually lives.
 *
 * Two views of the same answer: **Sheet** puts every P&ID the tag was found on
 * onto a canvas, **Knowledge Graph** shows the tag with those sheets and its
 * matched timeseries tags around it, with a Node Details panel.
 */
/**
 * Same reduction as the server's normalize_tag: upper-case, drop anything that
 * is not A-Z0-9. The parse emits several spellings of one device.
 */
const normTag = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, '')

const TagDetailPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams<{ tag: string }>()
  // React Router has already decoded the route param. Decoding again turns a
  // tag containing a literal percent ("0-100%") into a malformed escape and
  // throws URIError, taking the whole route down.
  const tag = params.tag ?? ''

  const canvasRef = useRef<CanvasHandle | null>(null)
  const hasFitRef = useRef(false)
  /**
   * Every tag opens on the board now, whatever it was reached from.
   *
   * A document-sourced row used to land on the Documents tab because the board
   * drew sheets only, so such a tag arrived at an empty canvas. The board draws
   * documents now, so that redirect became the thing standing between a
   * document-evidence row and the board and graph views of it. `?source` is
   * still honoured for a deep link that asks for the passages explicitly.
   */
  const [searchParams] = useSearchParams()
  const [view, setView] = useState<ViewMode>(
    searchParams.get('source') === 'passages' ? 'documents' : 'board',
  )
  const [search, setSearch] = useState('')
  const [sheets, setSheets] = useState<TagSheetRow[]>([])
  const [documents, setDocuments] = useState<TagDocumentRow[]>([])

  /**
   * The plant hierarchy the sheets hang off — fetched SEPARATELY from the page
   * load, and deliberately not awaited with it.
   *
   * `v2/ukg/hierarchy` was measured on dev at 45s-197s. Inside the page's
   * Promise.all it held the whole drill-down hostage for minutes: the board had
   * its sheets and could not draw them. Loading it on its own lets the graph
   * render immediately as a flat fan and deepen into site -> sheet -> tag when
   * the hierarchy lands, which is strictly better than showing nothing.
   */
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const hierarchy = await apiService.getUkgHierarchy()
        if (!cancelled) setSites((hierarchy.sites ?? []) as UkgHierarchySite[])
      } catch {
        // A missing hierarchy costs depth, not correctness — leave it flat.
        if (!cancelled) setSites([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tag])

  /**
   * The readable form of the tag under review. `tag` is the RAW parse content
   * (the list page navigates with it, because that is the identity), but a
   * merged region's raw and display forms normalize to different keys — so
   * comparing raw against display chips never matches, which showed the blob as
   * a SECOND chip beside its own readable name and put the outline on the wrong
   * one. Resolve it once here and use the display form everywhere it is shown.
   */
  const displayTag = useMemo(() => {
    const key = normTag(tag)
    for (const sheet of sheets) {
      const hit = (sheet.sheet_tags ?? []).find((t) => normTag(t.raw) === key)
      if (hit) return hit.display
    }
    return tag
  }, [sheets, tag])
  const [group, setGroup] = useState<MatchedTagGroup | null>(null)
  const [sites, setSites] = useState<UkgHierarchySite[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  /**
   * The sheets request failed, as distinct from this tag being on no sheet.
   *
   * `/tag-sheets` reads every content-node property the tenant owns and
   * normalizes each one to find this tag, which on dev is 895,086 rows to
   * return 11 — past the 30s proxy budget, so it returned nothing at all.
   * A caught failure left `sheets` empty and the board said "not found on any
   * sheet" about a tag that is on eleven.
   */
  const [sheetsFailed, setSheetsFailed] = useState(false)
  /** Bumped by the board's Retry, to re-run the load below. */
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!tag) return
    let cancelled = false
    void (async () => {
      try {
        setLoading(true)
        // The matched lookup is a courtesy — the drill-down still works for a
        // pending tag, which by definition has no matches.
        // Only what this page cannot render without. The plant hierarchy is
        // NOT in here on purpose — see below.
        const [sheetsResp, documentsResp, matchedResp] = await Promise.all([
          apiService.getTagSheets(tag),
          // Also optional: a tag can be real and simply undiscussed, which is
          // an empty Documents tab rather than a failed drill-down.
          apiService
            .getTagDocuments(tag)
            .catch(() => ({ tag, documents: [] as TagDocumentRow[] })),
          // Optional: a pending tag has no matches, and a failure here must not
          // blank the board — reporting "not on any sheet" when the sheets
          // request succeeded is worse than showing no match data.
          // The search is a substring match ranked by confidence, so ask for a
          // page rather than one row: a longer tag CONTAINING this one can
          // outrank it and would otherwise be the only candidate.
          apiService
            .getMatchedTags({ limit: 20, offset: 0, search: tag })
            .catch(() => ({ groups: [], total: 0 })),
        ])
        if (cancelled) return
        setSheets(sheetsResp.sheets)
        setSheetsFailed(false)
        setDocuments(documentsResp.documents)
        setGroup(matchedResp.groups.find((g) => g.tag === tag) ?? null)
      } catch (err) {
        if (cancelled) return
        // Clear first. On a tag switch these still hold the PREVIOUS tag's
        // sheets, and the canvas only shows the failure state for an empty
        // list — so keeping them renders one tag's drawings captioned as
        // another's, which is worse than the empty board this replaced.
        setSheets([])
        setDocuments([])
        setGroup(null)
        setSheetsFailed(true)
        toast.error(
          err instanceof Error
            ? err.message
            : t('data_catalog:tag_mapping.load_failed', {
                defaultValue: 'Could not load tag mappings.',
              }),
        )
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tag, t, reloadKey])

  /**
   * Open the sheet in the graph editor with the tag's node highlighted — the
   * same deep-link shape the P&ID connector navigation uses.
   */
  /**
   * The document shown in the overlay, or null. Putra, 2026-09-17: "Whenever
   * preview or board should not redirect to data and move the page there --
   * please either use overlay or open in new tab."
   */
  const [preview, setPreview] = useState<{
    id: string
    name: string
    /** The tag as THIS file spells it, so the preview can land on it. */
    highlightTag?: string
    /** A drawing's node on its graph, so the editor boxes the tag. */
    highlightNodeId?: string | null
  } | null>(null)

  /**
   * PREVIEW opens the overlay. Always, whatever the card is.
   *
   * It is the BOARD's preview, the one a board card opens (Pegah, 2026-10-13
   * review: every preview should look like the board's, not the overlay with
   * the controls outside). A drawing opens the way a board P&ID card does: its
   * GRAPH document, in the editor with Original | Detected | Review inside the
   * panel, boxing the tag's node. A document lands on its mention and a sheet
   * on its column. Editing still opens the graph editor (`openSheetInEditor`).
   */
  const userRole = useAppStore((st) => st.user?.role)
  const handleOpenSource = useCallback(
    (source: ConnectedSource) => {
      // A timeseries match whose file was deleted resolves to nothing -- silently
      // doing nothing beats opening an overlay onto a document that is not there.
      if (!source.documentId) return
      // A sheet's viewer sits behind `AnnotatorRestrictedRoute` on its own
      // route; the overlay has no route to guard, so the guard is here, with
      // the same notice (Abdullah, #34397).
      if (source.kind === 'timeseries' && isAnnotatorRole(userRole)) {
        notifyAnnotatorBlocked()
        return
      }
      // A board P&ID card is the drawing's GRAPH document; the drawing's own
      // id would open the bare PDF viewer instead of the editor.
      if (source.kind === 'sheet' && source.graphId) {
        setPreview({
          id: source.graphId,
          name: source.documentName ?? source.name,
          highlightNodeId: source.nodeId ?? null,
        })
        return
      }
      setPreview({
        id: source.documentId,
        name: source.documentName ?? source.name,
        // The tag as THIS file spells it: a drawing prints the tag under
        // review, a timeseries card is named after its column, and a document
        // card carries the mention it was found by.
        highlightTag:
          source.kind === 'sheet'
            ? displayTag
            : source.kind === 'timeseries'
              ? source.name
              : source.mention,
      })
    },
    [userRole, displayTag],
  )

  /** Editing a tag happens in the graph editor, which is a route, not a preview. */
  const openSheetInEditor = useCallback((source: ConnectedSource) => {
    if (!source.graphId || !source.documentId) return
    const query = new URLSearchParams({ editor: 'd3', documentId: source.documentId })
    if (source.nodeId) query.set('highlightNode', source.nodeId)
    // A tab rather than `navigate`: taking this page away is the thing Putra
    // asked to stop.
    window.open(
      `/graph-editor/${encodeURIComponent(source.graphId)}?${query.toString()}`,
      '_blank',
      'noopener,noreferrer',
    )
  }, [])

  /**
   * Open the document itself; a passage has no drawing node to highlight, so
   * there is nothing the editor would add -- the overlay is the whole answer.
   */
  const openDocument = useCallback(
    (documentId: string, documentName?: string, mention?: string | null) => {
      setPreview({ id: documentId, name: documentName ?? '', highlightTag: mention ?? undefined })
    },
    [],
  )

  /**
   * Everything this tag is connected to, as ONE list — the board, the graph and
   * every count read from it, so they cannot disagree. See connectedSources.ts.
   */
  const sources = useMemo(
    () => connectedSources({ sheets, documents, matches: group?.matches ?? [] }),
    [sheets, documents, group],
  )

  /**
   * The graph: the tag at the centre, every source it is connected to around it,
   * and the sites its drawings belong to above those.
   *
   * Built from `sources` — the SAME list the board draws — rather than from
   * `sheets` + matches directly. Building it separately is how the two tabs came
   * to disagree about what "connected" means: documents were missing here
   * entirely, so a tag the Matched table said was named in 32 documents had not
   * one of them on the graph.
   *
   * Filtered by the search box so the canvas responds to the same control the
   * list page uses.
   */
  const { nodes, edges } = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const matching = (label: string) => !needle || label.toLowerCase().includes(needle)

    const equipmentClass = group?.matches.find((m) => m.equipment_type)?.equipment_type ?? null
    const percent = (value: number) => `${Math.floor(value * 100)}%`

    const graphNodes: MockNode[] = [
      {
        id: TAG_NODE_ID,
        label: displayTag,
        type: 'functional_location',
        attributes: [{ label: 'Name', value: displayTag }],
      },
    ]
    const graphEdges: MockEdge[] = []

    // Which site each of the tag's drawings belongs to, so the graph is a tree
    // (site → sheet → tag → timeseries) rather than one flat fan. Sites with
    // none of this tag's sheets are left out.
    const siteOfDoc = new Map<string, UkgHierarchySite>()
    sites.forEach((site) =>
      site.documents.forEach((doc) => {
        if (doc.id) siteOfDoc.set(doc.id, site)
      }),
    )
    const seenSites = new Set<string>()

    for (const source of sources.filter((s) => matching(s.name))) {
      if (source.kind === 'timeseries') {
        graphNodes.push({
          id: source.id,
          label: source.name,
          type: 'components',
          attributes: [
            { label: 'Name', value: source.name },
            ...(source.matchMethod
              ? [{ label: 'Match method', value: source.matchMethod }]
              : []),
            ...(source.confidence !== undefined
              ? [{ label: 'Confidence', value: percent(source.confidence) }]
              : []),
          ],
        })
        graphEdges.push({
          id: `e-${source.id}`,
          source: TAG_NODE_ID,
          target: source.id,
          label: 'matched to',
        })
        continue
      }

      // A sheet and a document are both `documents` in the graph's vocabulary —
      // the legend has one Documents type and they are both files. What differs
      // is the edge: a drawing PRINTS the tag, a document discusses it.
      const site = source.documentId ? siteOfDoc.get(source.documentId) : undefined
      if (site && !seenSites.has(site.site_id)) {
        seenSites.add(site.site_id)
        graphNodes.push({
          id: `site:${site.site_id}`,
          label: site.name,
          type: 'site',
          attributes: [
            { label: 'Name', value: site.name },
            { label: 'Type', value: 'Site' },
            { label: 'Documents', value: String(site.document_count) },
          ],
        })
      }
      graphNodes.push({
        id: source.id,
        label: source.name,
        // The design's Core Attributes rows: Name / Equipment class / Type /
        // Status. Equipment class comes from the match the tag carries (the
        // matcher's detected class); it is omitted rather than faked when the
        // match has none.
        type: 'documents',
        attributes: [
          { label: 'Name', value: source.name },
          ...(equipmentClass ? [{ label: 'Equipment class', value: equipmentClass }] : []),
          { label: 'Type', value: 'Document' },
          { label: 'Status', value: 'Success' },
          // Only a document has these. A drawing's tag is an observation, not a
          // claim about identity, so it carries no confidence to show.
          ...(source.passageCount !== undefined
            ? [{ label: 'Passages', value: String(source.passageCount) }]
            : []),
          ...(source.confidence !== undefined
            ? [{ label: 'Confidence', value: percent(source.confidence) }]
            : []),
        ],
        attachedDocuments: [source.name],
      })
      if (site) {
        graphEdges.push({
          id: `e-site-${source.id}`,
          source: `site:${site.site_id}`,
          target: source.id,
          label: 'contains',
        })
      }
      graphEdges.push({
        id: `e-${source.id}`,
        source: source.id,
        target: TAG_NODE_ID,
        label: source.kind === 'sheet' ? 'appears on' : 'mentions',
      })
    }

    return { nodes: graphNodes, edges: graphEdges }
  }, [displayTag, sources, group, search, sites])

  // Legend starts with every type on; clicking one in TypesPanel filters it out.
  const [enabledTypes, setEnabledTypes] = useState<Set<MockNodeType>>(
    () => new Set<MockNodeType>(NODE_TYPES),
  )

  const handleToggleType = useCallback((type: MockNodeType) => {
    setEnabledTypes((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }, [])

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  )

  /**
   * Chips for the Tag section. A sheet node shows every device tag on that
   * sheet (with the reviewed tag outlined, per the design); anything else just
   * shows the tag under review. Kept out of MockNode so the Knowledge Graph
   * page's model stays as it is.
   */
  const selectedNodeTags = useMemo(() => {
    // Graph node ids ARE source ids now, so this is a lookup rather than a
    // prefix parse. Only a sheet carries chips; a document has no device list.
    const list = sources.find((s) => s.id === selectedNodeId)?.sheetTags ?? []
    if (list.length > 0) {
      const key = normTag(tag)
      const displays = list.map((t) => t.display)
      // Membership is decided on the RAW form; prepending on a display-form
      // miss is what duplicated a merged blob against its own readable name.
      return list.some((t) => normTag(t.raw) === key) ? displays : [displayTag, ...displays]
    }
    return [displayTag]
  }, [selectedNodeId, sources, tag, displayTag])

  /**
   * Edges touching the selected node in EITHER direction. Sheets now point at
   * the tag (site → sheet → tag), so an outgoing-only list would leave the tag
   * node claiming it has no connection to the sheet it sits on.
   */
  const selectedNodeConnections = useMemo(
    () =>
      edges
        .filter((edge) => edge.source === selectedNodeId || edge.target === selectedNodeId)
        .map((edge) => {
          const otherId = edge.source === selectedNodeId ? edge.target : edge.source
          const other = nodes.find((node) => node.id === otherId)
          return {
            id: edge.id,
            label: `${edge.label ?? 'linked'} → ${other?.label ?? otherId}`,
            targetId: otherId,
          }
        }),
    [edges, nodes, selectedNodeId],
  )

  /**
   * Frame the whole graph when the Knowledge Graph tab opens.
   *
   * `CanvasHandle.fit()` is NOT a true fit: it does `cy.fit()` and then zooms to
   * `fit × 1.73`, deliberately, so the Knowledge Graph page's readout
   * round-trips to "100%" on its big hierarchy. A tag's graph is three nodes in
   * a wide chain, and that 1.73× overshoot puts both neighbours off-screen — the
   * tag sits alone in the middle with its edges trailing off both sides, while
   * the readout claims 100%. Dividing the overshoot back out is what actually
   * frames it; verified in a browser at ~72%.
   *
   * The tab mounts the canvas fresh, so this must run after that mount rather
   * than on data load — hence the small delay.
   */
  useEffect(() => {
    if (view !== 'knowledge_graph' || nodes.length === 0) return
    // Fit ONCE per visit to the tab. `nodes` is recomputed as the search box
    // filters, so keying the fit off it re-centred and re-zoomed the graph on
    // every keystroke — the view kept jumping out from under the search.
    if (hasFitRef.current) return
    hasFitRef.current = true
    // The design opens this tab with a node selected and the details panel up.
    setSelectedNodeId((prev) => prev ?? TAG_NODE_ID)
    const timer = setTimeout(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      canvas.fit()
      canvas.setZoom(canvas.getZoom() / FIT_OVERSHOOT)
    }, 200)
    return () => clearTimeout(timer)
  }, [view, nodes])

  // Leaving the tab re-arms the fit, so coming back still opens framed.
  useEffect(() => {
    if (view !== 'knowledge_graph') hasFitRef.current = false
  }, [view])

  /**
   * Board layout. The design's Save button persists the arrangement, and there
   * is no server-side store for it, so it lives in localStorage keyed by tag.
   * That is a deliberate stopgap: it is per-browser, not per-tenant, and a
   * shared layout would need an API. Flagged rather than hidden.
   */
  const layoutKey = `tag-mapping:layout:${tag}`
  const [layout, setLayout] = useState<Record<string, CardPosition>>({})
  const [layoutDirty, setLayoutDirty] = useState(false)

  useEffect(() => {
    if (sources.length === 0) return
    let saved: Record<string, CardPosition> = {}
    try {
      saved = JSON.parse(localStorage.getItem(layoutKey) || '{}')
    } catch {
      saved = {}
    }
    // Saved positions win, but a source that appeared since the last save still
    // gets a default slot rather than vanishing off-canvas. That also absorbs
    // the layout-key change: a layout saved when cards were keyed by document
    // id simply no longer matches, so those cards take their default slot.
    setLayout({ ...defaultLayout(sources), ...saved })
    setLayoutDirty(false)
  }, [sources, layoutKey])

  const handleLayoutChange = useCallback((next: Record<string, CardPosition>) => {
    setLayout(next)
    setLayoutDirty(true)
  }, [])

  const handleSaveLayout = useCallback(() => {
    try {
      localStorage.setItem(layoutKey, JSON.stringify(layout))
      setLayoutDirty(false)
      toast.success(t('data_catalog:tag_mapping.detail.saved', { defaultValue: 'Layout saved.' }))
    } catch {
      toast.error(
        t('data_catalog:tag_mapping.detail.save_failed', {
          defaultValue: 'Could not save the layout.',
        }),
      )
    }
  }, [layout, layoutKey, t])

  /**
   * The sheet the current selection stands for, falling back to the first —
   * a `doc:` node names its own document, anything else (the site or tag node)
   * has no one sheet, and the tag is on all of them.
   */
  const selectedSource = useCallback(
    () => sources.find((source) => source.id === selectedNode?.id),
    [selectedNode, sources],
  )

  /**
   * The drawing behind the selection, for the two actions that need a GRAPH.
   *
   * Falls back to the tag's first sheet when the selected node has no drawing of
   * its own — the tag root, a site, or (now that they are on the graph) a
   * document or a timeseries.
   */
  const selectedSheet = useCallback(() => {
    const selected = selectedSource()
    if (selected?.kind === 'sheet') return selected
    return sources.find((source) => source.kind === 'sheet')
  }, [selectedSource, sources])

  /**
   * Editing a tag means editing it where it lives — the sheet's graph editor,
   * with the tag's node focused. Follows the selection rather than always
   * opening the first sheet, which sent you to the wrong drawing whenever the
   * tag appeared on more than one.
   */
  const handleEditTags = useCallback(() => {
    const sheet = selectedSheet()
    if (sheet) openSheetInEditor(sheet)
  }, [selectedSheet, openSheetInEditor])

  /**
   * Node Details footer -- the "Preview files" button Putra named directly.
   *
   * He asked for two things: stop "directly going to data", and land "zoomed in
   * to specific tag". A flat overlay answers the first and cannot answer the
   * second -- zoom-to-node lives in the editor, which needs the parsed graph
   * and its D3 renderer to place a node at all. `handleOpenSource` carries
   * `highlightNode`, so the editor does exactly what he asked; the only thing
   * wrong with it was that it took the page away.
   *
   * So this uses the other remedy he offered in the same breath -- "either use
   * overlay or open in new tab". A sheet opens a tab now, which means this
   * button both stops the redirect AND arrives zoomed on the tag. The overlay
   * stays for the Documents tab, where a passage has no node to zoom to and a
   * flat preview IS the whole answer.
   */
  const handleOpenRelated = useCallback(() => {
    if (!selectedNode) return
    // Preview the SELECTED file when the selection is one. Before documents
    // reached the graph this could only ever mean a sheet, so it always opened
    // the editor; selecting a document and being sent to an unrelated drawing
    // would be the wrong answer to "preview files".
    const source = selectedSource() ?? selectedSheet()
    if (source) handleOpenSource(source)
  }, [selectedNode, selectedSource, selectedSheet, handleOpenSource])

  return (
    <div className="flex h-screen">
      <AppSidebar />
      {/* UI-33: the detail view keeps the section nav too — every other Data
          detail surface (MatchEntity, UserArtifactViewer) renders it. */}
      <DataSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className={`${PAGE_HEADER} justify-between`}>
          <IntuiBreadcrumb
            items={[
              {
                label: t('data_catalog:tag_mapping.detail.breadcrumb', {
                  defaultValue: 'Tag Mapping',
                }),
                onSelect: () => {
                  const returnTo = searchParams.get('returnTo')
                  navigate(returnTo?.startsWith('/tag-mapping?') ? returnTo : '/tag-mapping')
                },
              },
              { label: displayTag },
            ]}
          />
        </header>

        <main className="flex min-h-0 flex-1 flex-col bg-background dark:bg-dark-card">
          {/* The rule under the toolbar (Putra, 2026-09-17: "Missing Border").
              The LIST page gets one for free -- its table's `<thead>` carries
              `border-b`, and the table starts immediately below. Here the tabs
              render a canvas or a graph, which have no header row, so without
              this the toolbar floats over the content with nothing closing it. */}
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
            <div className="relative w-[240px]">
              <Search className="absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <IntuiInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('data_catalog:tag_mapping.search_placeholder', {
                  defaultValue: 'Search',
                })}
                className="h-9 ps-9"
              />
            </div>
            <IntuiButton variant="outline" size="lg" disabled>
              <ListFilter className="size-4" />
              {t('data_catalog:tag_mapping.filter', { defaultValue: 'Filter' })}
            </IntuiButton>

            <IntuiTabs value={view} onValueChange={(value) => setView(value as ViewMode)}>
              <IntuiTabsList className="h-9">
                <IntuiTabsTrigger value="board" className="px-3">
                  {t('data_catalog:tag_mapping.detail.tabs.board', { defaultValue: 'Board' })}
                </IntuiTabsTrigger>
                <IntuiTabsTrigger value="knowledge_graph" className="px-3">
                  {t('data_catalog:tag_mapping.detail.tabs.knowledge_graph', {
                    defaultValue: 'Knowledge graph',
                  })}
                </IntuiTabsTrigger>
                <IntuiTabsTrigger value="documents" className="px-3">
                  {t('data_catalog:tag_mapping.detail.tabs.documents', {
                    defaultValue: 'Documents',
                  })}
                </IntuiTabsTrigger>
              </IntuiTabsList>
            </IntuiTabs>

            {/* Saves the board's card layout, so it means nothing on the
                other tabs. */}
            {view === 'board' && (
              <div className="ms-auto">
                <IntuiButton size="lg" onClick={handleSaveLayout} disabled={!layoutDirty}>
                  <Save className="size-4" />
                  {t('data_catalog:tag_mapping.detail.save', { defaultValue: 'Save' })}
                </IntuiButton>
              </div>
            )}
          </div>

          {view === 'documents' ? (
            <DocumentsPanel
              documents={documents}
              loading={loading}
              onOpenDocument={openDocument}
            />
          ) : view === 'board' ? (
            <SheetsCanvas
              sources={sources}
              loading={loading}
              failed={sheetsFailed}
              onRetry={() => setReloadKey((n) => n + 1)}
              layout={layout}
              onLayoutChange={handleLayoutChange}
              onOpenSource={handleOpenSource}
            />
          ) : (
            <div className="relative flex min-h-0 flex-1">
              <div className="relative min-h-0 min-w-0 flex-1 bg-muted/30">
                <TypesPanel
                  nodes={nodes}
                  enabledTypes={enabledTypes}
                  onToggle={handleToggleType}
                  hideCounts
                />
                <KnowledgeGraphCanvas
                  ref={canvasRef}
                  nodes={nodes}
                  edges={edges}
                  enabledTypes={enabledTypes}
                  showEdgeLabels
                  selectedNodeId={selectedNodeId}
                  onNodeSelect={setSelectedNodeId}
                />
                <div className="absolute bottom-3 left-3 z-10">
                  {/* No undo/redo here: the frame omits them on this tab, and
                      they were permanently disabled and wired to empty
                      handlers — two controls that could never do anything. */}
                  <KgZoomControls canvasRef={canvasRef} />
                </div>
              </div>
              {selectedNode && (
                <NodeDetailsPanel
                  className="w-[520px]"
                  // The frame's wording: this panel's node IS a tag here, so
                  // "Mapped tag" and "Tag sources" name what the sections hold.
                  title={t('data_catalog:tag_mapping.detail.node_details', {
                    defaultValue: 'Node details',
                  })}
                  attributesLabel={t('data_catalog:tag_mapping.detail.core_attributes', {
                    defaultValue: 'Core attributes',
                  })}
                  tagsLabel={t('data_catalog:tag_mapping.detail.mapped_tag', {
                    defaultValue: 'Mapped tag',
                  })}
                  connectionsLabel={t('data_catalog:tag_mapping.detail.tag_sources', {
                    defaultValue: 'Tag sources',
                  })}
                  plainAttributes
                  footerLabel={t('data_catalog:tag_mapping.detail.preview_files', {
                    defaultValue: 'Preview files',
                  })}
                  node={selectedNode}
                  connections={selectedNodeConnections}
                  onClose={() => setSelectedNodeId(null)}
                  onConnectionClick={setSelectedNodeId}
                  tags={selectedNodeTags}
                  activeTag={displayTag}
                  onOpenPreview={handleOpenRelated}
                  onEditTags={handleEditTags}
                  alwaysShowFooter
                />
              )}
            </div>
          )}
        </main>
      </div>

      {preview && (
        <SourceFilePreviewOverlay
          documentId={preview.id}
          name={preview.name}
          highlightTag={preview.highlightTag}
          highlightNodeId={preview.highlightNodeId}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  )
}

export default TagDetailPage
